<template>
  <div>
    <h3 class="pt-0 mt-0 pb-0">{{ $t("viewSettings.goods_export.title") }}</h3>
    <v-card elevation="0">
      <v-row class="ml-1">
        <v-col cols="8" lg="6">
          <v-text-field
            prepend-inner-icon="$avito"
            :label="$t('viewSettings.goods_export.avito_url')"
            v-model="avitoExportUrl"
            ref="avitoExportUrlTextToCopy"
            outlined
            hide-details
            append-icon="fa-clipboard"
            @click:append="copyText('avitoExportUrlTextToCopy')"
            @click="copyText('avitoExportUrlTextToCopy')"
            :readonly="true"
          ></v-text-field>
        </v-col>
        <v-col cols="4" lg="6">
          <v-btn
            @click="generate('avito')"
            class="mt-2"
            color="primary"
            elevation="0"
            :disabled="!isFetchedGoodsExportUrls"
          >
            {{ $t("common.generate") }}</v-btn
          >
          <v-btn
            class="mt-2 ml-2"
            :href="avitoExportUrl"
            target="_blank"
            :disabled="!avitoExportUrl || avitoExportUrl === ''"
            color="success"
            :title="$t('common.download_file')"
            elevation="0"
            ><v-icon small>fa-download</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="8" lg="6">
          <v-text-field
            prepend-inner-icon="$zzap"
            :label="$t('viewSettings.goods_export.zzap_url')"
            v-model="zzapExportUrl"
            ref="zzapExportUrlTextToCopy"
            outlined
            hide-details
            append-icon="fa-clipboard"
            @click:append="copyText('zzapExportUrlTextToCopy')"
            @click="copyText('zzapExportUrlTextToCopy')"
            :readonly="true"
          ></v-text-field>
        </v-col>
        <v-col cols="4" lg="6">
          <v-btn
            @click="generate('zzap')"
            class="mt-2"
            color="primary"
            elevation="0"
            :disabled="!isFetchedGoodsExportUrls"
          >
            {{ $t("common.generate") }}</v-btn
          >
          <v-btn
            class="mt-2 ml-2"
            :href="zzapExportUrl"
            target="_blank"
            :disabled="!zzapExportUrl || zzapExportUrl === ''"
            color="success"
            :title="$t('common.download_file')"
            elevation="0"
            ><v-icon small>fa-download</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="12">
          <v-btn :to="{name:'settings-goods-export-ozon-seller-api'}" class="mt-2" color="success" elevation="0">
            {{ $t("viewSettings.goods_export.export_to_ozon_via_seller_api") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
    <h3 class="pt-3 mt-0 pb-0">{{ $t("common.storages") }}</h3>
    <v-data-table
      :headers="[
        { text: this.$t('common.title'), value: 'name', sortable: true },
        {
          text: this.$t('viewSettings.goods_export.export_for_avito'),
          value: 'export.avito_export.is_enabled',
          sortable: false,
          class: 'header-avito',
        },
        {
          name: 'zzap',
          text: this.$t('viewSettings.goods_export.export_for_zzap'),
          value: 'export.zzap_export.is_enabled',
          sortable: false,
          class: 'header-zzap',
        },
        {
          name: 'ozon',
          text: this.$t('viewSettings.goods_export.export_for_ozon'),
          value: 'export.ozon_export.is_enabled',
          sortable: false,
          class: 'header-ozon',
        },
      ]"
      :items="storages"
      elevation="0"
      :loading="!storagesIsFetched"
      :footer-props="{
        'items-per-page-text': $t('tables.items_per_page'),
        'items-per-page-options': [
          10, 50, 100,
          //-1
        ],
      }"
    >
      <template v-slot:footer.page-text="props">
        {{
          $t("tables.x_of_y", [
            `${props.pageStart}-${props.pageStop}`,
            props.itemsLength,
          ])
        }}
      </template>
      <template v-slot:header.export.avito_export.is_enabled="props">
        <v-icon class="mr-2">$avito</v-icon>{{ props.header.text }}
      </template>
      <template v-slot:header.export.zzap_export.is_enabled="props">
        <v-icon class="mr-2">$zzap</v-icon>{{ props.header.text }}
      </template>
      <template v-slot:header.export.ozon_export.is_enabled="props">
        <v-icon class="mr-2">$ozon</v-icon>{{ props.header.text }}
      </template>
      <template v-slot:item="props">
        <tr>
          <td class="text-xs-left">
            {{ props.item.name }}
          </td>
          <td class="text-xs-left align-content-center">
            <div class="d-inline-block">
              <v-simple-checkbox
                :disabled="!!nowUpdatingExportForStorageWithId"
                v-if="nowUpdatingExportForStorageWithId !== props.item.id"
                @click="
                  toggle(
                    props.item.id,
                    'is_enabled',
                    !(
                      props.item.avito_export &&
                      props.item.avito_export.is_enabled
                    ),
                    'avito'
                  )
                "
                :value="
                  props.item.avito_export && props.item.avito_export.is_enabled
                "
              />
              <v-progress-circular
                indeterminate
                color="primary"
                size="20"
                v-if="nowUpdatingExportForStorageWithId === props.item.id"
              />
            </div>
            <v-btn
              v-if="nowUpdatingExportForStorageWithId !== props.item.id"
              elevation="0"
              min-width="36"
              min-height="36"
              color="primary"
              class="mb-0 ml-3 px-0 white--text"
              :title="$t('common.edit')"
              :to="{
                name: 'settings-goods-export-edit',
                params: { id: props.item.id, type: 'avito' },
              }"
            >
              <v-icon small>fa-cog</v-icon>
            </v-btn>
          </td>
          <td class="text-xs-left align-content-center">
            <div class="d-inline-block">
              <v-simple-checkbox
                :disabled="!!nowUpdatingExportForStorageWithId"
                v-if="nowUpdatingExportForStorageWithId !== props.item.id"
                @click="
                  toggle(
                    props.item.id,
                    'is_enabled',
                    !(
                      props.item.zzap_export &&
                      props.item.zzap_export.is_enabled
                    ),
                    'zzap'
                  )
                "
                :value="
                  props.item.zzap_export && props.item.zzap_export.is_enabled
                "
              />
              <v-progress-circular
                indeterminate
                color="primary"
                size="20"
                v-if="nowUpdatingExportForStorageWithId === props.item.id"
              />
            </div>
            <v-btn
              v-if="nowUpdatingExportForStorageWithId !== props.item.id"
              elevation="0"
              min-width="36"
              min-height="36"
              color="primary"
              class="mb-0 ml-3 px-0 white--text"
              :title="$t('common.edit')"
              :to="{
                name: 'settings-goods-export-edit',
                params: { id: props.item.id, type: 'zzap' },
              }"
            >
              <v-icon small>fa-cog</v-icon>
            </v-btn>
          </td>
          <td class="text-xs-left align-content-center">
            <div class="d-inline-block">
              <v-simple-checkbox
                :disabled="!!nowUpdatingExportForStorageWithId"
                v-if="nowUpdatingExportForStorageWithId !== props.item.id"
                @click="
                    toggle(
                      props.item.id,
                      'is_enabled',
                      !(
                        props.item.ozon_export &&
                        props.item.ozon_export.is_enabled
                      ),
                      'ozon'
                    )
                "
                :value="
                  props.item.ozon_export && props.item.ozon_export.is_enabled
                "
              />
              <v-progress-circular
                indeterminate
                color="primary"
                size="20"
                v-if="nowUpdatingExportForStorageWithId === props.item.id"
              />
            </div>
            <v-btn
              v-if="nowUpdatingExportForStorageWithId !== props.item.id"
              elevation="0"
              min-width="36"
              min-height="36"
              color="primary"
              class="mb-0 ml-3 px-0 white--text"
              :title="$t('common.edit')"
              :to="{
                name: 'settings-goods-export-edit',
                params: { id: props.item.id, type: 'ozon' },
              }"
            >
              <v-icon small>fa-cog</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
      <template v-slot:no-data>
        {{ $t("common.no_data_available") }}
      </template>
      <template v-slot:no-results>
        {{ $t("common.no_data_available") }}
      </template>
    </v-data-table>

  </div>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import i18n from "@/i18n";
import OzonSellerApiParametersForm from "@/components/storage/OzonSellerApiParametersForm";

export default {
  name: "Posts",
  components: { OzonSellerApiParametersForm },
  data: () => ({
    isDisabled: true,
    isVisibleOzonSellerApiParametersForm: false,
  }),
  created() {
    this.onLoad();
  },
  watch: {
    $route(to, from) {
      this.onLoad();
    },
  },
  computed: {
    ...mapFields("storages", { storagesIsFetched: "fetched" }),
    ...mapFields("storages", ["storages", "nowUpdatingExportForStorageWithId"]),
    ...mapFields("goods_export_urls", [
      "goodsExportUrls",
      "isFetchedGoodsExportUrls",
    ]),
    avitoExportUrl() {
      let obj = this.goodsExportUrls.find(
        (item) => item.export_name === "avito"
      );
      if (obj) {
        return `${this.$apiURL}/goods_feed/avito/${obj.random_string}/avito.csv`;
      }
      return null;
    },
    zzapExportUrl() {
      let obj = this.goodsExportUrls.find(
        (item) => item.export_name === "zzap"
      );
      if (obj) {
        return `${this.$apiURL}/goods_feed/zzap/${obj.random_string}/zzap.csv`;
      }
      return null;
    },
  },
  methods: {
    generate(exportName) {
      this.$confirm({
        title: this.$t("common.confirm"),
        text: this.$t("viewSettings.goods_export.confrm_generate_new_url"),
        cancelText: this.$t("common.no"),
        acceptText: this.$t("common.yes"),
      }).then(async (y) => {
        this.$store.dispatch("goods_export_urls/updateGoodsExportUrls", {
          export_name: exportName,
        });
      });
    },
    copyText(ref) {
      this.isDisabled = false;
      this.$nextTick(() => {
        let textToCopy = this.$refs[ref].$el.querySelector("input");
        textToCopy.select();
        document.execCommand("copy");
        this.isDisabled = true;
      });
    },
    onLoad() {
      this.$store.dispatch("storages/get");
      this.$store.dispatch("goods_export_urls/getGoodsExportUrls");
    },
    toggle(storage_id, parameter, value, export_name) {
      let parameters = {
        storage_id,
        export_name: export_name,
      };
      parameters[parameter] = value;
      this.$store.dispatch("storages/setGoodsExportParameters", parameters);
    },
  },
};
</script>

<style scoped></style>
